import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISS_KEY = 'pwa-install-dismissed-at';
const DISMISS_DAYS = 7;

// Event ini bisa muncul sebelum React selesai render, jadi ditangkap di level modul
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault(); // tahan banner bawaan browser, kita tampilkan toast sendiri
        deferredPrompt = event as BeforeInstallPromptEvent;
        listeners.forEach((notify) => notify());
    });

    window.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        listeners.forEach((notify) => notify());
    });
}

// Sudah dibuka sebagai aplikasi terpasang (bukan di tab browser)
const isStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

const isIos = () =>
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS

function recentlyDismissed(): boolean {
    try {
        const at = Number(localStorage.getItem(DISMISS_KEY));
        return Boolean(at) && Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
    } catch {
        return false;
    }
}

/**
 * Toast ajakan memasang aplikasi (PWA).
 * - Android/Chrome/Edge: tombol "Pasang" membuka dialog pemasangan bawaan browser.
 * - iPhone/iPad (Safari): tidak ada dialog otomatis, jadi ditampilkan petunjuk manual.
 * Tidak muncul kalau aplikasi sudah terpasang, dan disembunyikan 7 hari setelah ditutup.
 */
export default function InstallPrompt(): React.ReactElement | null {
    const [, setTick] = useState(0);
    const [hidden, setHidden] = useState(() => isStandalone() || recentlyDismissed());
    const [installing, setInstalling] = useState(false);

    useEffect(() => {
        const notify = () => setTick((tick) => tick + 1);
        listeners.add(notify);
        return () => {
            listeners.delete(notify);
        };
    }, []);

    const ios = isIos();
    const canPrompt = deferredPrompt !== null;

    if (hidden || (!canPrompt && !ios)) return null;

    const dismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
            // penyimpanan tidak tersedia: toast hanya ditutup untuk sesi ini
        }
        setHidden(true);
    };

    const install = async () => {
        const promptEvent = deferredPrompt;
        if (!promptEvent) return;

        setInstalling(true);
        try {
            await promptEvent.prompt();
            const choice = await promptEvent.userChoice;
            if (choice.outcome === 'dismissed') dismiss();
            else setHidden(true);
        } finally {
            // event hanya bisa dipakai sekali
            deferredPrompt = null;
            setInstalling(false);
        }
    };

    return (
        <div
            role="status"
            className="pointer-events-none fixed inset-x-0 bottom-0 z-[70] flex justify-center px-4"
            style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        >
            <div className="pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-2xl bg-slate-900 p-3 text-white shadow-xl">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600">
                    <Download className="h-5 w-5" />
                </span>

                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">Pasang aplikasi SIMPEG</p>
                    <p className="text-xs text-slate-300">
                        {ios
                            ? 'Ketuk tombol Bagikan, lalu pilih "Tambah ke Layar Utama".'
                            : 'Buka lebih cepat dari layar utama, tanpa membuka browser.'}
                    </p>
                </div>

                {!ios && (
                    <button
                        type="button"
                        onClick={() => void install()}
                        disabled={installing}
                        className="shrink-0 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-900 disabled:opacity-60"
                    >
                        Pasang
                    </button>
                )}

                <button type="button" onClick={dismiss} aria-label="Tutup" className="shrink-0 rounded-lg p-1.5 text-slate-300">
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}